import React, { useState, useEffect, useCallback } from 'react';
import { analyticsAPI, adminAPI, unwrapList, parsePaginated } from '../services/api';
import Pagination, { PAGE_SIZE, paginateItems } from './Pagination';

function filterPaymentsByPeriod(payments, period, monthFilter, yearFilter) {
  let filtered = Array.isArray(payments) ? [...payments] : [];

  if (period === 'week') {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    filtered = filtered.filter((payment) => new Date(payment.created_at) >= oneWeekAgo);
  } else if (period === 'month') {
    if (monthFilter !== 'all') {
      filtered = filtered.filter((payment) => {
        const paymentDate = new Date(payment.created_at);
        return (
          paymentDate.getMonth() + 1 === parseInt(monthFilter, 10) &&
          paymentDate.getFullYear() === parseInt(yearFilter, 10)
        );
      });
    } else {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      filtered = filtered.filter((payment) => new Date(payment.created_at) >= oneMonthAgo);
    }
  } else if (period === 'quarter') {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    filtered = filtered.filter((payment) => new Date(payment.created_at) >= threeMonthsAgo);
  } else if (period === 'year') {
    filtered = filtered.filter((payment) => {
      const paymentDate = new Date(payment.created_at);
      return paymentDate.getFullYear() === parseInt(yearFilter, 10);
    });
  }

  return filtered;
}

function Analytics() {
  const [period, setPeriod] = useState('month');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [membershipPayments, setMembershipPayments] = useState([]);
  const [salons, setSalons] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState('0.00');
  const [newPayment, setNewPayment] = useState({
    salon: '',
    salon_name: '',
    owner_name: '',
    start_date: '',
    end_date: '',
    amount_paid: '',
    payment_method: 'Bank Transfer'
  });
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  const fetchMembershipPayments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        pageSize: PAGE_SIZE,
        period,
      };
      if (period === 'month') {
        params.month = monthFilter;
        params.year = yearFilter;
      } else if (period === 'year') {
        params.year = yearFilter;
      }

      const response = await analyticsAPI.getMembershipPayments(params);
      const parsed = parsePaginated(response.data);

      if (parsed.isServerPaginated) {
        setMembershipPayments(parsed.results);
        setTotalCount(parsed.count);
        if (parsed.totalRevenue != null) {
          setTotalRevenue(parsed.totalRevenue.toFixed(2));
        } else {
          setTotalRevenue(
            parsed.results
              .reduce((sum, payment) => sum + parseFloat(payment.amount_paid || 0), 0)
              .toFixed(2)
          );
        }
        if (parsed.results.length === 0 && parsed.count > 0 && currentPage > 1) {
          setCurrentPage((page) => Math.max(1, page - 1));
        }
      } else {
        const filtered = filterPaymentsByPeriod(
          parsed.results,
          period,
          monthFilter,
          yearFilter
        );
        const { pageItems, total, currentPage: safePage } = paginateItems(
          filtered,
          currentPage
        );
        setMembershipPayments(pageItems);
        setTotalCount(total);
        setTotalRevenue(
          filtered
            .reduce((sum, payment) => sum + parseFloat(payment.amount_paid || 0), 0)
            .toFixed(2)
        );
        if (safePage !== currentPage) {
          setCurrentPage(safePage);
        }
      }
    } catch (err) {
      if (err?.response?.status === 404 && currentPage > 1) {
        setCurrentPage((page) => Math.max(1, page - 1));
        return;
      }
      setError('Failed to fetch membership payments data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, period, monthFilter, yearFilter]);

  useEffect(() => {
    fetchMembershipPayments();
  }, [fetchMembershipPayments]);

  const fetchSalons = useCallback(async ({ force = false } = {}) => {
    if (!force && salons.length > 0) {
      return salons;
    }
    try {
      const response = await adminAPI.getSalonOwners({
        page: 1,
        pageSize: 100,
        is_approved: true,
      });
      const list = unwrapList(response.data);
      setSalons(list);
      return list;
    } catch (err) {
      console.error('Failed to fetch salons:', err);
      return [];
    }
  }, [salons]);

  const openAddPaymentModal = async () => {
    setShowAddModal(true);
    await fetchSalons();
  };

  const handleEditClick = async (payment) => {
    setEditingPayment({ ...payment });
    setShowEditModal(true);
    await fetchSalons();
  };

  // Generate array of months for dropdown
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Generate array of years (last 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

  // Handle salon selection in add modal
  const handleSalonSelect = (e) => {
    const salonId = e.target.value;
    if (salonId) {
      const selectedSalon = salons.find(salon => salon.id.toString() === salonId);
      if (selectedSalon) {
        setNewPayment({
          ...newPayment,
          salon: salonId,
          salon_name: selectedSalon.salon_name,
          owner_name: selectedSalon.owner_name || ''
        });
      }
    } else {
      setNewPayment({
        ...newPayment,
        salon: '',
        salon_name: '',
        owner_name: ''
      });
    }
  };

  // Handle salon selection in edit modal
  const handleEditSalonSelect = (e) => {
    const salonId = e.target.value;
    if (salonId) {
      const selectedSalon = salons.find(salon => salon.id.toString() === salonId);
      if (selectedSalon) {
        setEditingPayment({
          ...editingPayment,
          salon: salonId,
          salon_name: selectedSalon.salon_name,
          owner_name: selectedSalon.owner_name || ''
        });
      }
    }
  };

  const handleAddPayment = async () => {
    if (!newPayment.salon || !newPayment.amount_paid) return;
    
    setIsLoading(true);
    try {
      await analyticsAPI.addMembershipPayment(newPayment);
      setShowAddModal(false);
      setNewPayment({
        salon: '',
        salon_name: '',
        owner_name: '',
        start_date: '',
        end_date: '',
        amount_paid: '',
        payment_method: 'Bank Transfer'
      });
      setCurrentPage(1);
      if (currentPage === 1) {
        await fetchMembershipPayments();
      }
    } catch (err) {
      setError('Failed to add payment');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Save edited payment
  const handleSaveEdit = async () => {
    if (!editingPayment.salon || !editingPayment.amount_paid) return;
    
    setIsLoading(true);
    try {
      await analyticsAPI.updateMembershipPayment(
        editingPayment.id, 
        editingPayment
      );
      setShowEditModal(false);
      setEditingPayment(null);
      await fetchMembershipPayments();
    } catch (err) {
      setError('Failed to update payment');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (payment) => {
    setPaymentToDelete(payment);
    setShowDeleteModal(true);
  };

  // Confirm delete payment
  const confirmDeletePayment = async () => {
    setIsLoading(true);
    try {
      await analyticsAPI.deleteMembershipPayment(paymentToDelete.id);
      setShowDeleteModal(false);
      setPaymentToDelete(null);
      await fetchMembershipPayments();
    } catch (err) {
      setError('Failed to delete payment');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">Platform Analytics</h1>
        <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
          <select 
            className="border rounded-md px-3 py-2 bg-white"
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
          
          {/* Show month filter when period is month */}
          {period === 'month' && (
            <>
              <select
                className="border rounded-md px-3 py-2 bg-white"
                value={monthFilter}
                onChange={(e) => {
                  setMonthFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Months</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              
              <select
                className="border rounded-md px-3 py-2 bg-white"
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </>
          )}
          
          {/* Show year filter when period is year */}
          {period === 'year' && (
            <select
              className="border rounded-md px-3 py-2 bg-white"
              value={yearFilter}
              onChange={(e) => {
                setYearFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      
      {/* Membership Payments Section */}
      <div className="card mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Membership Payments</h2>
          <button 
            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
            onClick={openAddPaymentModal}
          >
            Add Payment
          </button>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <div className="mb-4 p-4 bg-green-50 rounded-lg">
          <h3 className="text-green-800 font-medium">Total Revenue</h3>
          <p className="text-2xl font-bold text-green-700">₹{totalRevenue}</p>
        </div>
        
        {isLoading && !showAddModal && !showEditModal && !showDeleteModal ? (
          <p className="text-gray-500">Loading membership data...</p>
        ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    <th className="px-4 py-3">Salon</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Payment Method</th>
                    <th className="px-4 py-3">Period</th>
                    <th className="px-4 py-3">Created At</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {membershipPayments.length > 0 ? (
                    membershipPayments.map((payment) => (
                      <tr key={payment.id} className="text-sm hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{payment.salon_name}</td>
                        <td className="px-4 py-3 text-gray-600">{payment.owner_name}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">₹{payment.amount_paid}</td>
                        <td className="px-4 py-3 text-gray-600">{payment.payment_method}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {payment.start_date} to {payment.end_date}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDate(payment.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-2">
                            <button 
                              className="text-blue-600 hover:text-blue-800"
                              onClick={() => handleEditClick(payment)}
                            >
                              Edit
                            </button>
                            <button 
                              className="text-red-600 hover:text-red-800"
                              onClick={() => handleDeleteClick(payment)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-4 py-3 text-center text-gray-500">
                        No membership payment data available for the selected period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
        )}
        <Pagination page={currentPage} total={totalCount} onPageChange={setCurrentPage} />
      </div>
      
      {/* Add Payment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Add Membership Payment</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Select Salon</label>
                <select
                  className="w-full p-2 border rounded"
                  value={newPayment.salon}
                  onChange={handleSalonSelect}
                >
                  <option value="">-- Select a Salon --</option>
                  {salons.map(salon => (
                    <option key={salon.id} value={salon.id}>
                      {salon.salon_name} ({salon.owner_name})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Salon Name</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded bg-gray-100"
                  value={newPayment.salon_name}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Owner Name</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded bg-gray-100"
                  value={newPayment.owner_name}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Start Date</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={newPayment.start_date}
                  onChange={(e) => setNewPayment({...newPayment, start_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">End Date</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={newPayment.end_date}
                  onChange={(e) => setNewPayment({...newPayment, end_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Amount Paid</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newPayment.amount_paid}
                  onChange={(e) => setNewPayment({...newPayment, amount_paid: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Payment Method</label>
                <select
                  className="w-full p-2 border rounded"
                  value={newPayment.payment_method}
                  onChange={(e) => setNewPayment({...newPayment, payment_method: e.target.value})}
                >
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleAddPayment}
                disabled={isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Payment Modal */}
      {showEditModal && editingPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Edit Membership Payment</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Select Salon</label>
                <select
                  className="w-full p-2 border rounded"
                  value={editingPayment.salon}
                  onChange={handleEditSalonSelect}
                >
                  <option value="">-- Select a Salon --</option>
                  {salons.map(salon => (
                    <option key={salon.id} value={salon.id}>
                      {salon.salon_name} ({salon.owner_name})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Salon Name</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded bg-gray-100"
                  value={editingPayment.salon_name}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Owner Name</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded bg-gray-100"
                  value={editingPayment.owner_name}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Start Date</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={editingPayment.start_date}
                  onChange={(e) => setEditingPayment({...editingPayment, start_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">End Date</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={editingPayment.end_date}
                  onChange={(e) => setEditingPayment({...editingPayment, end_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Amount Paid</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingPayment.amount_paid}
                  onChange={(e) => setEditingPayment({...editingPayment, amount_paid: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Payment Method</label>
                <select
                  className="w-full p-2 border rounded"
                  value={editingPayment.payment_method}
                  onChange={(e) => setEditingPayment({...editingPayment, payment_method: e.target.value})}
                >
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleSaveEdit}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && paymentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Confirm Delete</h2>
            <p className="mb-6">
              Are you sure you want to delete the payment for {paymentToDelete.salon_name}?
              This action cannot be undone.
            </p>
            <div className="flex justify-end">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                onClick={confirmDeletePayment}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;




